// Chart-rank domain logic (Milestone M3).
//
// Apple's RSS marketing feeds list the current top-free / top-paid apps for a
// storefront. This module parses those feeds and matches a looked-up app to its
// rank by app id. It is intentionally pure and side-effect free so the parsing
// and matching rules can be unit tested without any network access.
//
// Honesty note: chart rank is a public ordering Apple itself publishes. It is
// NOT a download count and we never derive installs, revenue, or share from it.

/**
 * @typedef {object} ChartEntry
 * @property {number} rank - 1-based position in the feed (1 = top of chart).
 * @property {number} trackId - Apple app id (numeric).
 * @property {string|null} name - App name as the feed reports it.
 */

/**
 * @typedef {object} ChartRank
 * @property {'free'|'paid'} type
 * @property {number} rank - 1-based chart position.
 */

/**
 * @typedef {object} RowChart
 * @property {number|null} chartRankFree - 1-based top-free rank, or null.
 * @property {number|null} chartRankPaid - 1-based top-paid rank, or null.
 * @property {number|null} chartRankGrossing - 1-based top-grossing rank, or null.
 * @property {string} chartLabel - human label for the chart-rank cell.
 * @property {boolean} chartUnavailable - true when the chart could not be read.
 */

// Stable labels used by the UI and asserted by tests.
export const CHART_LABELS = Object.freeze({
  UNAVAILABLE: 'Chart unavailable',
  NOT_CHARTING: 'Not charting in top 100',
});

/**
 * Parse a raw Apple RSS feed payload into an ordered list of chart entries. The
 * 1-based rank is the entry's position in the feed.
 *
 * Two feed shapes are accepted, because Apple serves top-free / top-paid and
 * top-grossing from different hosts with different JSON shapes (see
 * normalizeFeedItems):
 *
 *   marketing-tools (top-free / top-paid):
 *     { feed: { results: [ { id: "284910350", name: "Yelp", ... }, ... ] } }
 *   legacy iTunes RSS (top-grossing):
 *     { feed: { entry:   [ { id: { attributes: { "im:id": "284910350" } }, ... } ] } }
 *
 * The app id is a string in practice; we coerce it to a number and drop any
 * entry without a usable numeric id so a malformed item cannot shift the ranks
 * of valid entries (rank is assigned from the original feed position).
 *
 * @param {any} payload - Parsed feed object, or null.
 * @returns {ChartEntry[]}
 */
export function parseChartFeed(payload) {
  const entries = [];
  normalizeFeedItems(payload).forEach((item, index) => {
    if (item.trackId === null) return;
    entries.push({
      rank: index + 1, // position in the published feed, 1-based
      trackId: item.trackId,
      name: item.name,
    });
  });
  return entries;
}

/**
 * Find the 1-based rank of an app id within a parsed chart feed.
 *
 * Matching rule (per docs/PLAN.md): match by app id only. Names are localized
 * and can collide, so they are never used for matching.
 *
 * @param {number|string} trackId
 * @param {ChartEntry[]} entries - output of parseChartFeed.
 * @returns {number|null} 1-based rank, or null when the app is not in the feed.
 */
export function findChartRank(trackId, entries) {
  const id = toNumberOrNull(trackId);
  if (id === null || !Array.isArray(entries)) return null;
  const hit = entries.find((entry) => entry.trackId === id);
  return hit ? hit.rank : null;
}

/**
 * Combine top-free, top-paid, and top-grossing rank results for one app/storefront
 * into the row-level chart fields and a single human label.
 *
 * Label precedence:
 * - If all three feeds failed -> "Chart unavailable".
 * - Else if the app charts in any feed -> e.g. "Top Free #3 · Top Grossing #5"
 *   (free first, then paid, then grossing).
 * - Else (at least one feed read, app not present) -> "Not charting in top N".
 *
 * @param {object} input
 * @param {number|null} input.freeRank - rank from the free feed, or null.
 * @param {number|null} input.paidRank - rank from the paid feed, or null.
 * @param {number|null} input.grossingRank - rank from the grossing feed, or null.
 * @param {boolean} input.freeOk - whether the free feed was read successfully.
 * @param {boolean} input.paidOk - whether the paid feed was read successfully.
 * @param {boolean} input.grossingOk - whether the grossing feed was read successfully.
 * @param {number} [input.limit=100] - feed depth, used in the "top N" copy.
 * @returns {RowChart}
 */
export function summarizeChart({
  freeRank = null,
  paidRank = null,
  grossingRank = null,
  freeOk = false,
  paidOk = false,
  grossingOk = false,
  limit = 100,
} = {}) {
  const chartRankFree = isRank(freeRank) ? freeRank : null;
  const chartRankPaid = isRank(paidRank) ? paidRank : null;
  const chartRankGrossing = isRank(grossingRank) ? grossingRank : null;

  // All three feeds failed -> unavailable, not "not charting".
  if (!freeOk && !paidOk && !grossingOk) {
    return {
      chartRankFree: null,
      chartRankPaid: null,
      chartRankGrossing: null,
      chartLabel: CHART_LABELS.UNAVAILABLE,
      chartUnavailable: true,
    };
  }

  const parts = [];
  if (chartRankFree !== null) parts.push(`Top Free #${chartRankFree}`);
  if (chartRankPaid !== null) parts.push(`Top Paid #${chartRankPaid}`);
  if (chartRankGrossing !== null) parts.push(`Top Grossing #${chartRankGrossing}`);

  if (parts.length === 0) {
    return {
      chartRankFree: null,
      chartRankPaid: null,
      chartRankGrossing: null,
      chartLabel: `Not charting in top ${limit}`,
      chartUnavailable: false,
    };
  }

  return {
    chartRankFree,
    chartRankPaid,
    chartRankGrossing,
    chartLabel: parts.join(' · '),
    chartUnavailable: false,
  };
}

/**
 * Best (lowest) numeric chart rank for a row, used for sorting. Returns null
 * when the row is not charting or has no chart data.
 * @param {{chartRankFree?: number|null, chartRankPaid?: number|null, chartRankGrossing?: number|null}} row
 * @returns {number|null}
 */
export function bestChartRank(row) {
  if (!row) return null;
  const ranks = [row.chartRankFree, row.chartRankPaid, row.chartRankGrossing].filter(isRank);
  return ranks.length ? Math.min(...ranks) : null;
}

/**
 * @typedef {object} ChartBrowserEntry
 * @property {number} rank - 1-based chart position.
 * @property {number} trackId - Apple app id (numeric).
 * @property {string|null} name
 * @property {string|null} artistName - Developer / seller name.
 * @property {string|null} artworkUrl - 100×100 artwork URL.
 * @property {string|null} primaryGenre - Name of the primary genre.
 * @property {string|null} genreId - Apple genre id string.
 */

/**
 * Parse a raw Apple RSS feed (either host shape) into richer chart browser
 * entries. See normalizeFeedItems for the two accepted shapes.
 * @param {any} payload
 * @returns {ChartBrowserEntry[]}
 */
export function parseChartBrowserFeed(payload) {
  const entries = [];
  normalizeFeedItems(payload).forEach((item, index) => {
    if (item.trackId === null) return;
    entries.push({
      rank: index + 1,
      trackId: item.trackId,
      name: item.name,
      artistName: item.artistName,
      artworkUrl: item.artworkUrl,
      primaryGenre: item.primaryGenre,
      genreId: item.genreId,
    });
  });
  return entries;
}

/**
 * @typedef {object} NormalizedFeedItem
 * @property {number|null} trackId
 * @property {string|null} name
 * @property {string|null} artistName
 * @property {string|null} artworkUrl - 100×100 artwork URL.
 * @property {string|null} primaryGenre
 * @property {string|null} genreId
 */

/**
 * Normalize either Apple feed shape into a flat list of items in feed order.
 * Items keep their original position so the caller assigns ranks consistently;
 * malformed items are returned with `trackId: null` so the caller can drop them
 * without disturbing the ranks of the valid entries around them.
 *
 *   marketing-tools `feed.results[]` items (top-free / top-paid):
 *     { id, name, artistName, artworkUrl100, genres: [{ name, genreId }] }
 *   legacy iTunes RSS `feed.entry[]` items (top-grossing):
 *     { "im:name": { label }, "im:artist": { label }, "im:image": [{ label }],
 *       id: { attributes: { "im:id" } }, category: { attributes: { "im:id", label } } }
 *
 * @param {any} payload
 * @returns {NormalizedFeedItem[]}
 */
export function normalizeFeedItems(payload) {
  const feed = payload && typeof payload === 'object' ? payload.feed : null;
  if (!feed || typeof feed !== 'object') return [];

  if (Array.isArray(feed.results)) {
    return feed.results.map((item) => {
      if (!item || typeof item !== 'object') return emptyFeedItem();
      const genres = Array.isArray(item.genres) ? item.genres : [];
      const firstGenre = genres[0] ?? null;
      return {
        trackId: toNumberOrNull(item.id),
        name: nonEmptyStringOrNull(item.name),
        artistName: nonEmptyStringOrNull(item.artistName),
        artworkUrl: nonEmptyStringOrNull(item.artworkUrl100),
        primaryGenre: firstGenre ? nonEmptyStringOrNull(firstGenre.name) : null,
        genreId: firstGenre ? nonEmptyStringOrNull(firstGenre.genreId) : null,
      };
    });
  }

  if (Array.isArray(feed.entry)) {
    return feed.entry.map((item) => {
      if (!item || typeof item !== 'object') return emptyFeedItem();
      const idAttrs = item.id && typeof item.id === 'object' ? item.id.attributes : null;
      const catAttrs = item.category && typeof item.category === 'object' ? item.category.attributes : null;
      return {
        trackId: toNumberOrNull(idAttrs ? idAttrs['im:id'] : null),
        name: nonEmptyStringOrNull(labelOf(item['im:name'])),
        artistName: nonEmptyStringOrNull(labelOf(item['im:artist'])),
        artworkUrl: pickArtwork(item['im:image']),
        primaryGenre: nonEmptyStringOrNull(catAttrs ? catAttrs.label : null),
        genreId: nonEmptyStringOrNull(catAttrs ? catAttrs['im:id'] : null),
      };
    });
  }

  return [];
}

function emptyFeedItem() {
  return {
    trackId: null,
    name: null,
    artistName: null,
    artworkUrl: null,
    primaryGenre: null,
    genreId: null,
  };
}

/** Read the `.label` of a legacy iTunes RSS `{ label }` node. */
function labelOf(node) {
  return node && typeof node === 'object' ? node.label : null;
}

/**
 * Pick the largest available artwork from a legacy `im:image[]` list, then
 * normalize it to the 100×100 variant used elsewhere. The list is ordered small
 * → large (53/75/100), so the last entry is the highest resolution Apple ships.
 * @param {any} images
 * @returns {string|null}
 */
function pickArtwork(images) {
  if (!Array.isArray(images) || images.length === 0) return null;
  const last = images[images.length - 1];
  const url = nonEmptyStringOrNull(labelOf(last));
  if (!url) return null;
  // Legacy URLs end in e.g. /100x100bb.png; rewrite any /NxNbb.* to 100x100 so
  // the artwork matches the marketing-tools feed's resolution and the snapshot
  // test's /100x100 assertion.
  return url.replace(/\/\d+x\d+bb\.(png|jpg|jpeg)$/i, '/100x100bb.$1');
}

function isRank(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  // Feed ids are numeric strings like "284910350"; reject anything non-numeric.
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function nonEmptyStringOrNull(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
